import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
    ComposedChart, Bar, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend, ReferenceLine,
} from "recharts";
import { CurrencyDollar, Target, Calculator, Trash, X } from "@phosphor-icons/react";

const CATEGORIES = [
    { key: "fuel", label: "Fuel" },
    { key: "breakdowns", label: "Breakdowns" },
    { key: "manpower", label: "Manpower" },
    { key: "claims", label: "Claims" },
    { key: "marine", label: "Marine" },
    { key: "plant_hire", label: "Plant Hire" },
    { key: "maintenance", label: "Maintenance" },
    { key: "other", label: "Other" },
];

const fmtZAR = (n) => `R ${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

/* ===================== Cost-per-ton chart ===================== */
export default function CostPerTonChart() {
    const { user } = useAuth();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // { type: 'budget'|'cost', vessel }

    const load = async () => {
        try {
            const r = await api.get("/finance/cost-per-ton", { params: { limit: 20 } });
            setRows(r.data || []);
        } catch { /* swallow */ }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const canEdit = ["admin", "manager", "finance"].includes(user?.role);
    const chartData = useMemo(() => rows.map((r) => ({
        name: r.vessel_name?.slice(0, 14) || r.vessel_id.slice(0, 8),
        cost_per_ton: r.cost_per_ton_zar,
        total_cost: r.total_cost_zar,
        tons: r.total_tons_loaded,
        budget: r.budget_zar,
    })), [rows]);

    const avgCpt = useMemo(() => {
        const valid = rows.filter((r) => r.cost_per_ton_zar > 0);
        if (valid.length === 0) return 0;
        return valid.reduce((a, r) => a + r.cost_per_ton_zar, 0) / valid.length;
    }, [rows]);

    return (
        <div className="space-y-3" data-testid="cost-per-ton-section">
            <div className="g-card overflow-hidden">
                <div className="g-card-header-dark">
                    <CurrencyDollar size={16} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide">Cost per Ton Loaded · last 20 vessels</div>
                    {avgCpt > 0 && <span className="ml-auto text-xs">Avg <b className="tabular">{fmtZAR(avgCpt)}/t</b></span>}
                </div>
                <div style={{ height: 320 }} className="p-3">
                    {loading ? (
                        <div className="h-full flex items-center justify-center text-slate-400 text-sm">Loading…</div>
                    ) : chartData.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-slate-400 text-sm">No completed vessels with cost data yet. Finance must capture a budget and actual costs.</div>
                    ) : (
                        <ResponsiveContainer>
                            <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 40 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} angle={-30} textAnchor="end" interval={0} height={60} />
                                <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#64748B" }} label={{ value: "R / ton", angle: -90, position: "insideLeft", fontSize: 10, fill: "#64748B" }} />
                                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#64748B" }} label={{ value: "Total cost (R)", angle: 90, position: "insideRight", fontSize: 10, fill: "#64748B" }} />
                                <Tooltip
                                    contentStyle={{ fontSize: 12 }}
                                    formatter={(v, name) => name === "Tons" ? [`${Math.round(v)} t`, name] : [fmtZAR(v), name]}
                                />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Bar yAxisId="right" dataKey="total_cost" name="Total cost" fill="#1E40AF" radius={[2, 2, 0, 0]} />
                                <Bar yAxisId="right" dataKey="budget" name="Budget" fill="#94A3B8" radius={[2, 2, 0, 0]} />
                                <Line yAxisId="left" type="monotone" dataKey="cost_per_ton" name="R / ton" stroke="#DC2626" strokeWidth={2.5} dot={{ r: 4 }} />
                                {avgCpt > 0 && <ReferenceLine yAxisId="left" y={avgCpt} stroke="#DC2626" strokeDasharray="4 4" label={{ value: `Avg ${fmtZAR(avgCpt)}/t`, fontSize: 10, fill: "#DC2626" }} />}
                            </ComposedChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            <div className="g-card overflow-hidden">
                <div className="g-card-header-dark">
                    <Calculator size={16} weight="fill" />
                    <div className="font-display font-bold uppercase tracking-wide">Vessel Budget vs Actual</div>
                    {canEdit && <span className="ml-auto text-xs">Finance · Manager · Admin can capture</span>}
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs tabular">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                            <tr>
                                <th className="text-left p-2">Vessel</th>
                                <th className="text-left p-2">Client</th>
                                <th className="text-right p-2">Tons</th>
                                <th className="text-right p-2">Budget</th>
                                <th className="text-right p-2">Actual</th>
                                <th className="text-right p-2">Variance</th>
                                <th className="text-right p-2">Cost/Ton</th>
                                {canEdit && <th className="text-center p-2">Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr><td colSpan={canEdit ? 8 : 7} className="text-center text-slate-400 py-8">No completed vessels.</td></tr>
                            )}
                            {rows.map((r) => {
                                const variance = (r.budget_zar || 0) - (r.total_cost_zar || 0);
                                const fav = variance >= 0;
                                return (
                                    <tr key={r.vessel_id} className="border-t border-slate-100" data-testid={`cost-row-${r.vessel_id}`}>
                                        <td className="p-2 font-semibold text-[var(--grindrod-navy)]">{r.vessel_name}</td>
                                        <td className="p-2 text-slate-600">{r.client_name}</td>
                                        <td className="p-2 text-right">{Math.round(r.total_tons_loaded).toLocaleString()}</td>
                                        <td className="p-2 text-right">{r.budget_zar ? fmtZAR(r.budget_zar) : <span className="text-slate-400">—</span>}</td>
                                        <td className="p-2 text-right">{r.total_cost_zar ? fmtZAR(r.total_cost_zar) : <span className="text-slate-400">—</span>}</td>
                                        <td className={`p-2 text-right font-semibold ${fav ? "text-[var(--grindrod-green)]" : "text-[var(--grindrod-red)]"}`}>
                                            {r.budget_zar ? `${fav ? "" : "-"}${fmtZAR(Math.abs(variance))}` : "—"}
                                        </td>
                                        <td className="p-2 text-right font-semibold text-[var(--grindrod-navy)]">{r.cost_per_ton_zar ? `${fmtZAR(r.cost_per_ton_zar)}/t` : "—"}</td>
                                        {canEdit && (
                                            <td className="p-2 text-center">
                                                <div className="inline-flex gap-1.5">
                                                    <button onClick={() => setModal({ type: "budget", vessel: r })} className="btn-outline !py-1 !px-2 text-[10px]" data-testid={`set-budget-${r.vessel_id}`}>
                                                        <Target size={10} className="inline" /> Budget
                                                    </button>
                                                    <button onClick={() => setModal({ type: "cost", vessel: r })} className="btn-navy !py-1 !px-2 text-[10px]" data-testid={`add-cost-${r.vessel_id}`}>
                                                        <CurrencyDollar size={10} className="inline" /> Cost
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {modal?.type === "budget" && <BudgetModal vessel={modal.vessel} onClose={() => setModal(null)} onSaved={load} />}
            {modal?.type === "cost" && <CostModal vessel={modal.vessel} onClose={() => setModal(null)} onSaved={load} />}
        </div>
    );
}

/* ===================== Modals ===================== */
function ModalShell({ title, onClose, children }) {
    return (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="g-card w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="g-card-header-dark flex items-center">
                    <div className="font-display font-black uppercase tracking-wide flex-1">{title}</div>
                    <button onClick={onClose} className="text-white"><X size={16} /></button>
                </div>
                <div className="p-4">{children}</div>
            </div>
        </div>
    );
}

function BudgetModal({ vessel, onClose, onSaved }) {
    const [total, setTotal] = useState(vessel.budget_zar || 0);
    const [breakdown, setBreakdown] = useState({});
    const [notes, setNotes] = useState("");

    useEffect(() => {
        api.get(`/vessels/${vessel.vessel_id}/budget`).then((r) => {
            if (r.data) {
                setTotal(r.data.total_budget_zar || 0);
                setBreakdown(r.data.breakdown || {});
                setNotes(r.data.notes || "");
            }
        }).catch(() => {});
    }, [vessel.vessel_id]);

    const submit = async () => {
        try {
            await api.post(`/vessels/${vessel.vessel_id}/budget`, { total_budget_zar: parseFloat(total) || 0, breakdown, notes });
            toast.success("Budget saved");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    };

    const breakdownSum = Object.values(breakdown).reduce((a, v) => a + (parseFloat(v) || 0), 0);

    return (
        <ModalShell title={`Set Budget — ${vessel.vessel_name}`} onClose={onClose}>
            <div className="space-y-3 text-sm">
                <div>
                    <label className="uppercase-label block mb-1">Total Budget (ZAR) *</label>
                    <input type="number" min="0" step="1000" className="g-input tabular text-lg font-display font-bold"
                           value={total} onChange={(e) => setTotal(e.target.value)} data-testid="budget-total-input" />
                </div>
                <div>
                    <div className="uppercase-label mb-1">Breakdown by category (optional)</div>
                    <div className="grid grid-cols-2 gap-2">
                        {CATEGORIES.map((c) => (
                            <div key={c.key}>
                                <label className="text-[10px] text-slate-500 block">{c.label}</label>
                                <input type="number" min="0" step="1000" className="g-input tabular !py-1.5 text-xs"
                                       value={breakdown[c.key] || ""}
                                       onChange={(e) => setBreakdown({ ...breakdown, [c.key]: parseFloat(e.target.value) || 0 })}
                                       data-testid={`budget-cat-${c.key}`} />
                            </div>
                        ))}
                    </div>
                    {breakdownSum > 0 && (
                        <div className="text-[10px] text-slate-500 mt-1.5">Breakdown total: <b className="tabular">{fmtZAR(breakdownSum)}</b></div>
                    )}
                </div>
                <div><label className="uppercase-label block mb-1">Notes</label>
                    <input className="g-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>
                <div className="flex gap-2 justify-end pt-2">
                    <button onClick={onClose} className="btn-outline">Cancel</button>
                    <button onClick={submit} className="btn-primary" data-testid="budget-submit">Save</button>
                </div>
            </div>
        </ModalShell>
    );
}

function CostModal({ vessel, onClose, onSaved }) {
    const [items, setItems] = useState([]);
    const [adding, setAdding] = useState({ category: "fuel", amount_zar: 0, description: "" });

    const reload = async () => {
        try {
            const r = await api.get(`/vessels/${vessel.vessel_id}/costs`);
            setItems(r.data?.items || []);
        } catch { /* swallow */ }
    };

    useEffect(() => { reload(); }, [vessel.vessel_id]);

    const add = async () => {
        if (!adding.amount_zar || adding.amount_zar <= 0) return toast.error("Enter an amount");
        try {
            await api.post(`/vessels/${vessel.vessel_id}/costs`, adding);
            setAdding({ category: adding.category, amount_zar: 0, description: "" });
            await reload();
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/vessels/${vessel.vessel_id}/costs/${id}`);
            await reload(); onSaved();
        } catch { /* swallow */ }
    };

    const total = items.reduce((a, c) => a + (c.amount_zar || 0), 0);
    const cpt = vessel.total_tons_loaded ? total / vessel.total_tons_loaded : 0;

    return (
        <ModalShell title={`Actual Costs — ${vessel.vessel_name}`} onClose={onClose}>
            <div className="space-y-3 text-sm">
                <div className="bg-slate-50 rounded-sm p-2.5 text-xs flex justify-between">
                    <span>Tons loaded: <b className="tabular">{Math.round(vessel.total_tons_loaded).toLocaleString()} t</b></span>
                    <span>Total cost: <b className="tabular">{fmtZAR(total)}</b></span>
                    <span>Cost / ton: <b className="tabular text-[var(--grindrod-red)]">{fmtZAR(cpt)}/t</b></span>
                </div>

                <div className="grid grid-cols-[1fr_120px_80px] gap-2 items-end">
                    <div>
                        <label className="text-[10px] text-slate-500 block">Category</label>
                        <select className="g-input !py-1.5 text-xs" value={adding.category} onChange={(e) => setAdding({ ...adding, category: e.target.value })} data-testid="cost-category">
                            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] text-slate-500 block">Amount (ZAR)</label>
                        <input type="number" min="0" step="100" className="g-input tabular !py-1.5 text-xs" value={adding.amount_zar} onChange={(e) => setAdding({ ...adding, amount_zar: parseFloat(e.target.value) || 0 })} data-testid="cost-amount" />
                    </div>
                    <button onClick={add} className="btn-primary !py-1.5 text-xs" data-testid="cost-add">Add</button>
                </div>
                <input className="g-input !py-1.5 text-xs" placeholder="Description (optional)" value={adding.description} onChange={(e) => setAdding({ ...adding, description: e.target.value })} />

                <div className="border border-slate-200 rounded-sm overflow-hidden">
                    <table className="w-full text-xs tabular">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                            <tr><th className="text-left p-2">Category</th><th className="text-left p-2">Description</th><th className="text-right p-2">Amount</th><th className="w-8"></th></tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={4} className="text-center text-slate-400 p-4">No costs logged.</td></tr>
                            )}
                            {items.map((c) => (
                                <tr key={c.id} className="border-t border-slate-100">
                                    <td className="p-2 capitalize">{c.category.replace("_", " ")}</td>
                                    <td className="p-2 text-slate-600">{c.description || "—"}</td>
                                    <td className="p-2 text-right font-semibold">{fmtZAR(c.amount_zar)}</td>
                                    <td className="p-2"><button onClick={() => remove(c.id)} className="text-slate-400 hover:text-[var(--grindrod-red)]"><Trash size={12} /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="flex justify-end pt-2">
                    <button onClick={onClose} className="btn-outline">Close</button>
                </div>
            </div>
        </ModalShell>
    );
}
